import React, { useContext, useEffect, useRef, useState } from 'react'
import { X, Settings, ChevronRight, ChevronLeft } from 'lucide-react'
import { CantiqueContextAPI } from '../Context/CantiqueContext'
import { useTheme } from '../Context/ThemeContext'

const themeOptions = [
  { id: 'orange', label: 'Orange clair', background: '#ffb56b', color: '#1d1d1d' },
  { id: 'black', label: 'Noir', background: '#111111', color: '#f6f6f6' },
  { id: 'white', label: 'Blanc', background: '#ffffff', color: '#111111' },
  { id: 'blue', label: 'Bleu nuit', background: '#0b3d91', color: '#f5f5f5' },
  { id: 'green', label: 'Vert doux', background: '#2f4f4f', color: '#f9f5ef' }
]

const normalizeText = (text = '') => String(text)
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9\s&]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const defaultProjectionSong = {
  title: 'À Jésus, je m’abandonne',
  couplets: [
    { text: 'À Jésus, je m’abandonne, ce qu’il me dit, je le crois, et je prends ce qu’il me donne, la couronne avec la croix.' },
    { text: 'Il est mon refuge certain, je ne crains ni peine ni tourment, je sais qu’il me guide chaque matin, ma force, mon chant permanent.' },
    { text: 'Au cœur du soir et au cœur du jour, sa parole éclaire mes pas, je vis dans son amour, toujours, il me relève quand je trépas.' },
    { text: 'Quand le doute veut m’envahir, je regarde vers son visage, je m’abandonne et je respire sa paix, sa grâce, son courage.' },
    { text: 'Dans l’allégresse ou l’épreuve, je garde mon regard levé, je marche avec lui sans trêve, il est ma route, il est ma vérité.' }
  ],
  refrain: {
    text: 'Compter sur lui d’heure en heure, tant que dure le combat, que l’on vive ou que l’on meure, compter sur lui, tout est là.'
  }
}

const Projections = ({ isOpen, onClose }) => {
  const { allCantiques, selectedCantique, selectCantique } = useContext(CantiqueContextAPI)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isVisible, setIsVisible] = useState(isOpen)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const { theme: appTheme } = useTheme()
  const [themeId, setThemeId] = useState(() => (appTheme === 'dark' ? 'black' : 'white'))
  const themeChosenRef = useRef(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [textSize, setTextSize] = useState(6.5)
  const [currentVerse, setCurrentVerse] = useState(0)
  const [pinnedIds, setPinnedIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('projectionPinned') || '[]')
    } catch (_err) {
      return []
    }
  })
  const overlayRef = useRef(null)

  // the projection background follows the app theme until one is picked in the settings
  useEffect(() => {
    if (!themeChosenRef.current) setThemeId(appTheme === 'dark' ? 'black' : 'white')
  }, [appTheme])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement))
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  useEffect(() => {
    try {
      localStorage.setItem('projectionPinned', JSON.stringify(pinnedIds))
    } catch (_err) {
      // ignore
    }
  }, [pinnedIds])

  useEffect(() => {
    setCurrentVerse(0)
  }, [selectedCantique?._id, selectedCantique?.id])

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      if (overlayRef.current) {
        overlayRef.current.requestFullscreen().catch(() => {})
      }
    } else {
      setIsVisible(false)
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {})
      }
    }
  }, [isOpen])

  if (!isOpen && !isVisible) return null

  const theme = themeOptions.find((t) => t.id === themeId) || themeOptions[2]
  const availableCantiques = allCantiques
    .filter((cantique) => {
      if (!searchQuery.trim()) return true
      const normalizedSearch = normalizeText(searchQuery)
      const searchableFields = [
        cantique.title,
        cantique.number,
        cantique.langue,
        cantique.refrain?.text,
        ...(cantique.couplets || []).map((c) => c.text),
      ]
      const searchableText = searchableFields.map((field) => normalizeText(field)).join(' ')
      return searchableText.includes(normalizedSearch)
    })

  const pinnedCantiques = pinnedIds
    .map((id) => allCantiques.find((cantique) => cantique.id === id || cantique._id === id))
    .filter(Boolean)

  const togglePin = (id) => {
    setPinnedIds((current) => {
      if (current.includes(id)) {
        return current.filter((pinnedId) => pinnedId !== id)
      }
      if (current.length >= 5) return current
      return [...current, id]
    })
  }

  const handleSelectCantique = (id) => {
    if (selectedCantique?.id === id) return
    selectCantique(id)
    setCurrentVerse(0)
  }

  const currentCantique = selectedCantique || {
    title: defaultProjectionSong.title,
    couplets: defaultProjectionSong.couplets,
    refrain: defaultProjectionSong.refrain
  }

  const currentCouplet = currentCantique.couplets?.[currentVerse] || currentCantique.couplets?.[0] || null
  const coupletNumber = currentCouplet?.numero ?? currentVerse + 1
  const coupletText = currentCouplet?.text || currentCantique.couplets?.[0]?.text || ''
  const refrain = currentCantique.refrain?.text || currentCantique.refrain || ''

  const nextVerse = () => {
    setCurrentVerse((current) => {
      if (!currentCantique.couplets) return 0
      return (current + 1) % currentCantique.couplets.length
    })
  }

  const prevVerse = () => {
    setCurrentVerse((current) => {
      if (!currentCantique.couplets) return 0
      return (current - 1 + currentCantique.couplets.length) % currentCantique.couplets.length
    })
  }

  return (
    <div
      ref={overlayRef}
      className={`projectionOverlay ${isFullscreen ? 'projectionOverlay--fullscreen' : ''} ${isVisible ? 'projectionOverlay--visible' : 'projectionOverlay--hidden'}`}
      style={{ background: theme.background, color: theme.color }}
    >
      <div className="projectionContainer">
        <div className="projectionOverlay__header">
          <div className="projectionOverlay__header__close" onClick={onClose}>
            <span>
              <X size={18} />
            </span>
            <span>Fermer</span>
          </div>
          <div className="projectionOverlay__header__functions">
            <div className="projectionOverlay__header__functions__settings">
              <button
                type="button"
                className="projectionOverlay__header__functions__settings__btn"
                onClick={() => setSettingsOpen((open) => !open)}
                aria-label="Ouvrir les options de projection"
              >
                <Settings size={20} />
              </button>
            </div>
          </div>
        </div>

        {settingsOpen && (
          <>
            <div className="projectionOverlay__settingsBackdrop" onClick={() => setSettingsOpen(false)} />
            <div className="projectionOverlay__settingsPanel" onClick={(e) => e.stopPropagation()}>
              <div className="projectionOverlay__settingsSection">
              <h3>1) Thème de fond</h3>
              <div className="projectionOverlay__themeCards">
                {themeOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`projectionOverlay__themeCard ${themeId === option.id ? 'active' : ''}`}
                    onClick={() => {
                      themeChosenRef.current = true
                      setThemeId(option.id)
                    }}
                    style={{ background: option.background, color: option.color }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="projectionOverlay__settingsSection">
              <h3>3) Taille du texte</h3>
              <div className="projectionOverlay__textSizeControls">
                <button type="button" onClick={() => setTextSize((size) => Math.max(3, size - 0.5))}>-</button>
                <span>{textSize.toFixed(1)}em</span>
                <button type="button" onClick={() => setTextSize((size) => Math.min(10, size + 0.5))}>+</button>
              </div>
            </div>

            <div className="projectionOverlay__settingsSection">
              <h3>2) Cantiques épinglés</h3>
              <div className="projectionOverlay__pinnedRow">
                <span>Épinglés</span>
                <span>{pinnedCantiques.length}/5</span>
              </div>
              <div className="projectionOverlay__pinnedCards">
                {pinnedCantiques.length > 0 ? (
                  pinnedCantiques.map((cantique) => (
                    <button
                      key={cantique.id}
                      type="button"
                      className="projectionOverlay__pinnedCard"
                      onClick={() => handleSelectCantique(cantique.id)}
                    >
                      <strong>{cantique.number || '-'}</strong>
                      <span>{cantique.title}</span>
                      <small>{cantique.langue}</small>
                    </button>
                  ))
                ) : (
                  <div className="projectionOverlay__emptyState">Aucun cantique épinglé</div>
                )}
              </div>

              <div className="projectionOverlay__searchBar">
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un cantique"
                />
              </div>

              <div className="projectionOverlay__miniList">
                {availableCantiques.length > 0 ? (
                  availableCantiques.map((cantique) => {
                    const isPinned = pinnedIds.includes(cantique.id)
                    return (
                      <div key={cantique.id} className="projectionOverlay__miniListItem">
                        <button
                          type="button"
                          className="projectionOverlay__miniListItem__link"
                          onClick={() => handleSelectCantique(cantique.id)}
                        >
                          <span>{cantique.number || '-'}</span>
                          <span>{cantique.title}</span>
                        </button>
                        <button
                          type="button"
                          className={`projectionOverlay__pinButton ${isPinned ? 'active' : ''}`}
                          onClick={() => togglePin(cantique.id)}
                        >
                          {isPinned ? 'Épinglé' : 'Épingler'}
                        </button>
                      </div>
                    )
                  })
                ) : (
                  <div className="projectionOverlay__emptyState">Aucun résultat</div>
                )}
              </div>
            </div>
          </div>
        </>
        )}

        <div className="projectionOverlay__center">
          <div className="projectionOverlay__center__container">
            <div className="projectionOverlay__center__arrow" onClick={prevVerse}>
              <ChevronLeft size={60} />
            </div>
            <div className="projectionOverlay__center__content" style={{ fontSize: `${textSize}em` }}>
              {currentCantique ? (
                <>
                  <div className="projectionOverlay__center__couplet">
                    <span>{coupletNumber}</span>-
                    {coupletText || currentCantique.title}
                  </div>
                  {refrain && (
                    <div className="projectionOverlay__center__refrain">
                      <strong>Refrain</strong>
                      <p>{refrain}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="projectionOverlay__center__couplet">
                  <span>1</span>-
                  À Jésus, je m’abandonne Ce qu’il me dit, Je le crois Et je prends ce qu’il me donne La couronne avec la croix.
                </div>
              )}
            </div>
            <div className="projectionOverlay__center__arrow" onClick={nextVerse}>
              <ChevronRight size={60} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Projections